import { ISummary } from '../redux/summaries/types';
import appConfigData from '../appConfig.json';
import { ICreditPackage } from '../types/billing';
import type { IRedisKeyItem, IRedisKeysResponse, RedisKeysScope } from '../data-types';

interface IApiResponse<T> {
  payload: T;
  message?: string;
}

/**
 * Gets the API base URL from configuration
 */
const getApiUrl = () => {
  return (appConfigData as any).API_GW_REST_API_URL ?? (appConfigData as any).REST_API_URL;
};

/**
 * Makes an authenticated request to the API for JSON data
 */
const makeJsonRequest = async <T>(url: string, method: string, JWT: string, body?: any): Promise<T> => {
  const response = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${JWT}`,
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

  const data: IApiResponse<T> = await response.json();
  return data.payload;
};

/**
 * Creates a new summary
 * @param data - Summary creation payload containing recordId
 */
export interface CreateSummaryPayload {
  recordId: string;
  direction: string;
}

/**
 * POST /summaries
 * Creates a new summary
 */
export const createSummary = async (JWT: string, payload: CreateSummaryPayload): Promise<ISummary> => {
  const url = `${getApiUrl()}/summaries`;
  return makeJsonRequest(url, 'POST', JWT, payload);
};

/**
 * GET /summaries/:summaryId
 * Returns a specific summary
 */
export const getSummary = async (JWT: string, summaryId: string): Promise<ISummary> => {
  const url = `${getApiUrl()}/summaries/${summaryId}`;
  return makeJsonRequest(url, 'GET', JWT);
};

/**
 * Returns a PDF URL for summaryId (Note: The PDF will be opened in new tab (S3 object has Content-Disposition: inline)
 * @param summaryId - ID of summary to download
 * @param JWT - JSON Web Token for authentication
 */
export const handlePdfDownload = async (summaryId: string, JWT: string): Promise<string | null> => {
  try {
    const url = `${getApiUrl()}/summaries/${summaryId}/pdf`;
    const response = await makeJsonRequest<{ pdf_url: string }>(url, 'GET', JWT);
    if (!response.pdf_url) throw new Error('No PDF URL in response');
    return response.pdf_url;
  } catch (error) {
    console.error('Error opening PDF:', error);
    return null;
  }
};

/**
 * DELETE /summaries/:summaryId
 * Deletes a summary by ID
 * @param JWT - JWT token for authentication
 * @param summaryId - ID of the summary to delete
 * @returns Promise<string> - ID of the deleted summary
 */
export const deleteSummary = async (JWT: string, summaryId: string): Promise<string> => {
  const url = `${getApiUrl()}/summaries/${summaryId}`;
  return makeJsonRequest(url, 'DELETE', JWT);
};

/**
 * GET /summaries
 * Returns all summaries for an record
 *
 * Response:
 * {
 *   message: string,
 *   payload: [{
 *     id: string,
 *     record_id: string,
 *     created_at: string,
 *   }]
 * }
 */
export const getRecordSummaries = async (JWT: string, recordId: string): Promise<ISummary[]> => {
  const url = `${getApiUrl()}/summaries?recordId=${recordId}`;
  return makeJsonRequest(url, 'GET', JWT);
};

/**
 * POST /upload or /rag/upload
 * Uploads a file to the server with retry logic for throttling
 * @param JWT - Authentication token
 * @param file - File to upload
 * @param recordId - Optional record ID (required only for regular uploads)
 * @param fileName - Optional file name override
 * @param path - Optional backend rest api path prefix (e.g., 'rag' for RAG uploads)
 */
export const uploadFile = async ({
  JWT,
  file,
  recordId,
  fileName,
  path,
}: {
  JWT: string;
  file: File;
  recordId?: string;
  fileName?: string;
  path?: string;
}): Promise<{ message: string; fileKey: string }> => {
  if (((!recordId || recordId === 'undefined') && !path) || !fileName || fileName === 'undefined') {
    console.warn('Warning: recordId or fileName is falsy or undefined');
  }

  const uploadOperation = async () => {
    // Determine the endpoint path - default to /upload, or use /rag/upload if path includes 'rag'
    const endpoint = `${(path && `/${path}`) || ''}/upload`;

    // Build query parameters
    const queryParams = new URLSearchParams();
    if (recordId) queryParams.append('recordId', recordId);
    queryParams.append('fileName', fileName || file.name);
    queryParams.append('fileType', file.type || 'application/octet-stream');

    const url = `${getApiUrl()}${endpoint}?${queryParams.toString()}`;
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${JWT}` },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  };

  return retryWithBackoff(uploadOperation);
};

/**
 * POST /upload
 * Uploads base64 image data to the server with retry logic for throttling
 */
export const uploadContent = async ({
  JWT,
  content,
  recordId,
  fileName,
}: {
  JWT: string;
  content: string;
  fileName: string;
  recordId?: string;
}): Promise<{ message: string; fileKey: string }> => {
  const uploadOperation = async () => {
    const fileType = 'image/png';

    // Convert base64 to blob
    const base64Data = content.split(',')[1];
    const byteCharacters = atob(base64Data);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    const blob = new Blob(byteArrays, { type: fileType });

    const formData = new FormData();
    formData.append('file', blob);

    const url = `${getApiUrl()}/upload?recordId=${recordId}&fileName=${fileName}&fileType=${fileType}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${JWT}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  };

  return retryWithBackoff(uploadOperation);
};

/**
 * Retry helper with exponential backoff for throttling errors
 */
const retryWithBackoff = async <T>(operation: () => Promise<T>, maxRetries: number = 3, baseDelay: number = 1000): Promise<T> => {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error: any) {
      console.error(error);
      // Only retry throttling errors (500/429), not permanent errors like 413 (Payload Too Large)
      const isThrottlingError = (error.message?.includes('status: 500') || error.message?.includes('status: 429')) && !error.message?.includes('status: 413');

      if (attempt === maxRetries || !isThrottlingError) {
        throw error;
      }

      const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 1000; // Exponential backoff with jitter
      console.warn(`Upload attempt ${attempt + 1} failed with throttling, retrying in ${Math.round(delay)}ms...`);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  throw new Error('Max retries exceeded');
};

/*=========================================
  Billing API types - To the backend
=========================================*/

/**
 * GET /billing/packages
 * Fetches available credit packages (public endpoint)
 */
export const getCreditPackages = async (): Promise<ICreditPackage[]> => {
  const url = `${getApiUrl()}/billing/packages`;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const data: IApiResponse<ICreditPackage[]> = await response.json();
  return data.payload;
};

/**
 * POST /billing/create-payment-intent
 * Creates a Stripe payment intent for a credit package
 */
export const createPaymentIntent = async (JWT: string, packageId: string): Promise<string> => {
  const url = `${getApiUrl()}/billing/create-payment-intent`;
  const data = await makeJsonRequest<{ clientSecret: string }>(url, 'POST', JWT, { packageId });
  return data.clientSecret;
};

/*=========================================
  Control Plane API types - To the backend
=========================================*/

export type { IRedisKeyItem, IRedisKeysResponse, RedisKeysScope };

/**
 * GET /control-plane/redis/keys
 */
export const listRedisKeys = async (
  JWT: string, 
  scope: RedisKeysScope, 
  page: number = 1, 
  pageSize: number = 100
): Promise<IRedisKeysResponse> => {
  const url = `${getApiUrl()}/control-plane/redis/keys?scope=${encodeURIComponent(scope)}&page=${page}&pageSize=${pageSize}`;
  return makeJsonRequest(url, 'GET', JWT);
};

/**
 * DELETE /control-plane/redis/keys
 */
export const deleteRedisKeys = async (JWT: string, scope: RedisKeysScope): Promise<{ deletedCount: number }> => {
  const url = `${getApiUrl()}/control-plane/redis/keys?scope=${encodeURIComponent(scope)}`;
  return makeJsonRequest(url, 'DELETE', JWT);
};

/*=========================================
  Document Management API - To the backend
=========================================*/

export interface IDocumentItem {
  document_id: string;
  file_name: string;
  file_key: string;
  file_type: string;
  file_size: number;
  created_at: string;
}

/**
 * GET /rag/documents
 * Lists all RAG documents for the authenticated tenant
 */
export const listDocuments = async (JWT: string): Promise<IDocumentItem[]> => {
  const url = `${getApiUrl()}/rag/documents`;
  return makeJsonRequest(url, 'GET', JWT);
};

/**
 * DELETE /rag/documents/:documentId
 * Deletes a specific RAG document by ID
 */
export const deleteDocument = async (JWT: string, documentId: string): Promise<{ message: string; document_id: string }> => {
  const url = `${getApiUrl()}/rag/documents/${documentId}`;
  return makeJsonRequest(url, 'DELETE', JWT);
};
